import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface MarketplaceListing {
  id: string;
  name: string;
  description: string;
  category: string;
  author_name: string;
  pricing_model: string;
  price_usd?: number;
  rating_avg: number;
  rating_count: number;
  install_count: number;
  status: string;
  created_at: string;
}

interface MarketplaceCategory {
  id: string;
  name: string;
  description: string;
}

export function Marketplace() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('popular');

  const { data: categoriesData } = useQuery({
    queryKey: ['marketplace-categories'],
    queryFn: () => api<{ categories: MarketplaceCategory[] }>('marketplace/categories'),
  });

  const { data: listingsData, isLoading } = useQuery({
    queryKey: ['marketplace-search', search, selectedCategory, sortBy],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (selectedCategory) params.set('category', selectedCategory);
      params.set('sort', sortBy);
      return api<{ listings: MarketplaceListing[] }>(`marketplace/search?${params.toString()}`);
    },
  });

  const categories = categoriesData?.categories ?? [];
  const listings = listingsData?.listings ?? [];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">Marketplace</h1>
        <p className="text-muted-foreground">Discover and publish AI solutions for MSPs.</p>
      </div>

      {/* Quick actions */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => navigate('/marketplace/publish')}
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium"
        >
          Publish Solution
        </button>
        <button
          onClick={() => navigate('/marketplace/my-listings')}
          className="px-4 py-2 rounded-md text-sm font-medium border hover:bg-accent transition-colors"
        >
          My Listings
        </button>
      </div>

      {/* Search and filters */}
      <div className="flex gap-4 mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search solutions..."
          className="flex-1 border rounded-md px-3 py-2 bg-background text-sm"
        />
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="border rounded-md px-3 py-2 bg-background text-sm min-w-[160px]"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="border rounded-md px-3 py-2 bg-background text-sm min-w-[140px]"
        >
          <option value="popular">Most Popular</option>
          <option value="newest">Newest</option>
          <option value="rating">Top Rated</option>
        </select>
      </div>

      {/* Listings grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : listings.length === 0 ? (
        <div className="rounded-lg border bg-card p-12 text-center text-muted-foreground">
          {search ? `No solutions found for "${search}"` : 'No solutions published yet. Be the first!'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map((listing) => (
            <div
              key={listing.id}
              onClick={() => navigate(`/marketplace/${listing.id}`)}
              className="rounded-lg border bg-card p-5 hover:border-primary/50 transition-colors cursor-pointer"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-medium text-foreground">{listing.name}</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {listing.category}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {listing.description}
              </p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>by {listing.author_name}</span>
                <div className="flex items-center gap-3">
                  {listing.rating_count > 0 && (
                    <span>{listing.rating_avg.toFixed(1)} ({listing.rating_count})</span>
                  )}
                  <span>
                    {listing.pricing_model === 'free' ? 'Free' : `$${listing.price_usd?.toFixed(2) ?? '0'}`}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
